import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useProjects } from "@/contexts/ProjectContext";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import PageHeader from "@/components/dashboard/PageHeader";
import StatusBadge from "@/components/dashboard/StatusBadge";
import EmptyState from "@/components/dashboard/EmptyState";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const SEV_ORDER = { high: 0, medium: 1, low: 2 };

export default function SEOAuditPage() {
  const { activeProjectId } = useProjects();
  const [issues, setIssues] = useState([]);

  const load = useCallback(async () => {
    if (!activeProjectId) return;
    const { data } = await api.get(`/audit/${activeProjectId}`);
    setIssues([...data].sort((a, b) => (SEV_ORDER[a.severity] ?? 9) - (SEV_ORDER[b.severity] ?? 9)));
  }, [activeProjectId]);
  useEffect(() => { load(); }, [load]);

  const createTask = async (id) => {
    await api.post(`/audit/${id}/create-task`);
    toast.success("Task created from issue");
  };

  const counts = issues.reduce((a, x) => ({ ...a, [x.severity]: (a[x.severity] || 0) + 1 }), {});

  return (
    <DashboardLayout>
      <PageHeader
        overline="Technical SEO Audit"
        title="Every fix mapped to estimated impact."
        description="Severity-ordered. One click turns any issue into a tracked task."
      />

      <div className="grid grid-cols-3 gap-4 mb-6">
        {["high", "medium", "low"].map((s) => (
          <div key={s} className="bg-white border border-border rounded-sm p-5" data-testid={`audit-count-${s}`}>
            <p className="editorial-overline">{s} severity</p>
            <p className="font-serif text-3xl mt-2">{counts[s] || 0}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-border rounded-sm overflow-hidden">
        {issues.length === 0 ? (
          <EmptyState
            icon={ShieldCheck}
            title="No audit issues yet"
            description="Once a crawl completes, every issue will appear here ranked by severity, with a one-click 'Create task' button."
            testId="audit-empty-state"
          />
        ) : (
        <table className="w-full text-sm" data-testid="audit-table">
          <thead className="bg-ivory-100 text-left text-[11px] uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Severity</th>
              <th className="px-4 py-3">Issue</th>
              <th className="px-4 py-3">Affected</th>
              <th className="px-4 py-3">Impact</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {issues.map((i) => (
              <tr key={i.id} className="border-t border-border" data-testid={`audit-row-${i.id}`}>
                <td className="px-4 py-3"><StatusBadge status={i.severity} /></td>
                <td className="px-4 py-3">
                  <p className="font-medium text-navy-900">{i.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{i.recommended_fix}</p>
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground font-mono">{i.affected_url}</td>
                <td className="px-4 py-3 capitalize">{i.estimated_impact}</td>
                <td className="px-4 py-3">
                  <button onClick={() => createTask(i.id)} data-testid={`audit-create-task-${i.id}`} className="text-xs px-3 py-1.5 bg-navy-900 hover:bg-navy-800 text-ivory-50 rounded-sm">
                    Create task
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </div>
    </DashboardLayout>
  );
}
